// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tabs } from '@shared/ui-kit/components/tabs';
import { OpenTrades, useOpenTrades } from '@advisor-app/features/operations';
import { Trades } from '@advisor-app/features/trades';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import { renderColumnsTradesHistory } from '@advisor-app/features/trades-history/components';
import {
  renderColumnsTrader,
  useTrader,
  TraderAllocation,
  TraderBacktest,
  TraderPortfolioProvider,
  TraderSimulationAlerts,
} from '@advisor-app/features/operations';
import { type FC } from 'react';
import { Navigate } from 'react-router';
import { useAppSelector } from '@advisor-app/store/store';
import { selectClientId } from '@shared/data-access/slices/clients/clients-selectors';
import { useTranslation } from 'react-i18next';
import { useAdvisorRole } from '@advisor-app/hooks';
import { useAdvisoryProposalAccounts } from '@shared/advisory/proposals/hooks/use-advisory-proposal-accounts';
import type { ColumnMethods } from '@advisor-app/features/trades';

export const Trader: FC = () => {
  const { t } = useTranslation();
  const { canUseTrader, canSendProposals } = useAdvisorRole();
  const selectedClientId = useAppSelector(selectClientId);

  const {
    handleDeleteAllTrades,
    handleDeleteTrade,
    handleAccountChange,
    kpiData,
    sortedTrades,
    tradesHistory,
    mape,
    trades,
    areSomeTradeUnavailable,
    debtAmount,
    isLoadingTrades,
    proposalOperations,
    hasRiskProfile,
    validProposal,
  } = useTrader();

  const { kpiData: openTradesKpis, trades: openTrades } = useOpenTrades();

  const { ibans: advisedIbans } = useAdvisoryProposalAccounts(selectedClientId);

  if (!canUseTrader) return <Navigate to="/" replace />;

  if (isLoadingTrades) return null;

  return (
    <TraderPortfolioProvider
      clientId={selectedClientId}
      operations={proposalOperations}
    >
      <PageContainer className="mt-0">
        <Tabs.Root defaultValue="trades" state-id="trader">
          <Tabs.List>
            <Tabs.Trigger value="trades">
              {t('common.trades', { ns: 'common' })}
            </Tabs.Trigger>
            {openTrades.length > 0 && (
              <Tabs.Trigger value="open trades">
                {t('common.open-trades', { ns: 'common' })}
              </Tabs.Trigger>
            )}
            {tradesHistory.length > 0 && (
              <Tabs.Trigger value="trades history">
                {t('common.trades-history', { ns: 'common' })}
              </Tabs.Trigger>
            )}
            {trades.length > 0 && (
              <>
                <Tabs.Trigger
                  value="allocation"
                  disabled={proposalOperations.length === 0}
                >
                  {t('common.allocation', { ns: 'common' })}
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="backtest"
                  disabled={proposalOperations.length === 0}
                >
                  {t('common.backtest', { ns: 'common' })}
                </Tabs.Trigger>
              </>
            )}
          </Tabs.List>

          <Tabs.Content value="trades">
            <Trades
              tableColumns={renderColumnsTrader}
              columnMethods={{
                tickerOperation: (ticker: string, operation: any, index?: number) =>
                  handleDeleteTrade(ticker, operation, index),
                onAccountChange: handleAccountChange,
                advisedIbans,
              }}
              tableData={sortedTrades}
              kpisDataArray={kpiData}
              executeTrades={{
                data: trades,
                mape: mape,
                areSomeTradeUnavailable,
                debt: debtAmount,
              }}
              handleDeleteAllTrades={handleDeleteAllTrades}
              excelFileName="trades"
              showExecuteTrades
              showDeleteAllTrades
              showFormAddTicker
              showDownload
              showSendProposals={
                canSendProposals && trades.length > 0 && hasRiskProfile
              }
              proposalOperations={proposalOperations}
              format="trader"
              state-id="trader-trades-table"
              className="mt-12"
              validProposal={validProposal}
              alerts={<TraderSimulationAlerts />}
            />
          </Tabs.Content>

          <Tabs.Content value="trades history">
            <Trades
              tableColumns={(columnMethods: ColumnMethods) =>
                renderColumnsTradesHistory(columnMethods, t)
              }
              tableData={tradesHistory}
              kpisDataArray={[]}
              excelFileName="trades-history"
              showDownload
              showTablePagination
              initialSortBy={[{ id: 'date', desc: true }]}
              state-id="trader-history-table"
            />
          </Tabs.Content>

          <Tabs.Content value="open trades">
            <OpenTrades kpiData={openTradesKpis} trades={openTrades} />
          </Tabs.Content>

          <Tabs.Slot value={['allocation', 'backtest']}>
            <Tabs.Content value="allocation">
              <TraderAllocation />
            </Tabs.Content>

            <Tabs.Content value="backtest">
              <TraderBacktest />
            </Tabs.Content>
          </Tabs.Slot>
        </Tabs.Root>
      </PageContainer>
    </TraderPortfolioProvider>
  );
};

export default Trader;
